import type { Brand } from "../types/brand";
import type { Brand7dSummary, ScoreWeekly } from "../store/scores/types";

/**
 * Optional metrics used for the trailing visibility figure and descending sort.
 * Pass `null`/`undefined` to keep catalog order and omit scores.
 */
export type CreateOptionsFromBrandsScores =
  | ScoreWeekly[]
  | Record<number, Brand7dSummary>
  | null
  | undefined;

export type BrandSelectOption = {
  id: number;
  label: string;
  value: number;
  /** 7d / weekly visibility; higher sorts first when `scores` is set. */
  score?: number;
};

/**
 * Maps brands to select options. When `scores` is set, resolves visibility per brand
 * (weekly row match vs. keyed 7d summary) and returns options sorted by that value descending.
 */
export function createOptionsFromBrands(
  brands: Brand[] | null,
  scores?: CreateOptionsFromBrandsScores,
): BrandSelectOption[] {
  if (brands == null || brands.length === 0) {
    return [];
  }

  const sortByVisibility = scores != null;

  const options: BrandSelectOption[] = brands.map(({ id, name }) => {
    let score: number | undefined;

    if (sortByVisibility) {
      if (Array.isArray(scores)) {
        score = scores.find((row) => row.brand_id === id)?.current_week_avg_visibility;
      } else {
        score = scores[id]?.avg_visibility;
      }
    }

    return { id, label: name, value: id, score };
  });

  if (!sortByVisibility) {
    return options;
  }

  return [...options].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}
